import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useState } from "react";
import EditIcon from "@mui/icons-material/Edit";
import CancelIcon from "@mui/icons-material/Cancel";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

const COLUMN_COUNT = 10;

// Each entry maps the CURRENT status to the statuses it can move to, and the
// backend `action` that produces that status (per POST /predictions/basket/{id}/status/).
const STATUS_ACTIONS = {
  DRAFT: [
    { action: "PUBLISH", status: "PUBLISHED" },
    { action: "INACTIVE", status: "INACTIVE" },
  ],
  PUBLISHED: [
    { action: "INACTIVE", status: "INACTIVE" },
    { action: "CLOSE", status: "CLOSED" },
  ],
  INACTIVE: [
    { action: "ACTIVE", status: "PUBLISHED" },
    { action: "CLOSE", status: "CLOSED" },
  ],
  CLOSED: [],
};

const ACTION_LABELS = {
  PUBLISH: "Publish",
  ACTIVE: "Activate",
  INACTIVE: "Deactivate",
  CLOSE: "Close",
};

const thSx = {
  background: "#04343a",
  color: "#fff",
  fontWeight: 700,
  fontSize: "12px",
  whiteSpace: "nowrap",
  borderRight: "1px solid rgba(255,255,255,0.15)",
  "&:last-child": { borderRight: "none" },
  py: "6px",
  px: "10px",
};

const tdSx = {
  borderRight: "1px solid #e2e8f0",
  "&:last-child": { borderRight: "none" },
  py: "6px",
  px: "10px",
};

const STATUS_COLORS = {
  DRAFT: { bg: "#f1f5f9", color: "#475569" },
  PUBLISHED: { bg: "#e6f4ea", color: "#1a7f3c" },
  CLOSED: { bg: "#fdecea", color: "#c0392b" },
  INACTIVE: { bg: "#f1f5f9", color: "#94a3b8" },
};

const StatusBadge = ({ status, hasActions, onClick }) => {
  const { bg, color } = STATUS_COLORS[status] || STATUS_COLORS.DRAFT;
  return (
    <Typography
      component="button"
      onClick={hasActions ? onClick : undefined}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        fontSize: "11px",
        fontWeight: 700,
        color,
        background: bg,
        border: "none",
        borderRadius: "6px",
        px: 1,
        py: 0.3,
        cursor: hasActions ? "pointer" : "default",
        "&:hover": hasActions ? { filter: "brightness(0.95)" } : {},
      }}
    >
      {status || "—"}
      {hasActions && <ArrowDropDownIcon sx={{ fontSize: 16 }} />}
    </Typography>
  );
};

const formatCurrency = (value) =>
  value != null && value !== "" ? `₹${Number(value).toLocaleString("en-IN")}` : "—";

const formatPercent = (value) =>
  value != null && value !== "" ? `${Number(value).toFixed(2)}%` : "—";

const BasketTable = ({
  baskets,
  loading,
  loadingDetailId,
  onEdit,
  onStatusAction,
  onFetchBasketDetail,
  onManualClose,
}) => {
  const [confirmAction, setConfirmAction] = useState(null);
  const [closeReason, setCloseReason] = useState("");
  const [closeMethod, setCloseMethod] = useState(null);
  const [manualHoldings, setManualHoldings] = useState([]);
  const [manualPrices, setManualPrices] = useState({});
  const [manualLoading, setManualLoading] = useState(false);
  const [statusMenu, setStatusMenu] = useState(null);

  const closeStatusMenu = () => setStatusMenu(null);

  const filledManualHoldings = manualHoldings.filter((h) => manualPrices[h.symbol]);
  const manualExitingAll =
    manualHoldings.length > 0 &&
    filledManualHoldings.length === manualHoldings.length;

  const closeConfirmDialog = () => {
    setConfirmAction(null);
    setCloseReason("");
    setCloseMethod(null);
    setManualHoldings([]);
    setManualPrices({});
  };

  const selectManualMethod = async () => {
    setCloseMethod("MANUAL");
    setManualLoading(true);
    try {
      const detail = await onFetchBasketDetail(confirmAction.basket.id);
      const holdings = detail?.holdings || [];
      setManualHoldings(holdings);
      setManualPrices(
        Object.fromEntries(holdings.map((h) => [h.symbol, ""])),
      );
    } finally {
      setManualLoading(false);
    }
  };

  const handleConfirm = () => {
    if (confirmAction.action === "CLOSE" && closeMethod === "MANUAL") {
      const exitPrices = filledManualHoldings.map((h) => ({
        symbol: h.symbol,
        exit_price: Number(manualPrices[h.symbol]),
      }));
      onManualClose(
        confirmAction.basket,
        exitPrices,
        closeReason,
        manualExitingAll,
      );
    } else {
      onStatusAction(
        confirmAction.basket,
        confirmAction.action,
        confirmAction.action === "CLOSE" ? closeReason : undefined,
      );
    }
    closeConfirmDialog();
  };

  return (
    <Box
      sx={{
        background: "#fff",
        borderRadius: "12px",
        border: "1px solid #e2e8f0",
        boxShadow: "0 1px 6px rgba(0,0,0,0.07)",
        overflow: "hidden",
      }}
    >
      <TableContainer>
        <Table size="small" sx={{ borderCollapse: "collapse" }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...thSx, width: 48 }}>SNo.</TableCell>
              <TableCell sx={thSx}>Basket Name</TableCell>
              <TableCell sx={{ ...thSx, width: 110 }}>Strategy</TableCell>
              <TableCell sx={{ ...thSx, width: 100 }}>Risk Level</TableCell>
              <TableCell sx={{ ...thSx, width: 70, textAlign: "center" }}>Stocks</TableCell>
              <TableCell sx={{ ...thSx, width: 110 }}>Min. Investment</TableCell>
              <TableCell sx={{ ...thSx, width: 110 }}>Potential Return</TableCell>
              <TableCell sx={{ ...thSx, width: 100 }}>Status</TableCell>
              <TableCell sx={{ ...thSx, width: 70 }}>Free</TableCell>
              <TableCell sx={{ ...thSx, width: 90, textAlign: "center" }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} sx={{ textAlign: "center", py: 8 }}>
                  <Typography sx={{ color: "#94a3b8", fontSize: "13px" }}>
                    Loading...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : !Array.isArray(baskets) || baskets.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} sx={{ textAlign: "center", py: 8 }}>
                  <Typography sx={{ color: "#94a3b8", fontSize: "13px" }}>
                    No baskets yet. Create one!
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              baskets.map((basket, idx) => (
                <TableRow
                  key={basket.id}
                  sx={{
                    "&:hover": { background: "#f8fafc" },
                    transition: "background 0.15s",
                    borderBottom: "1px solid #e2e8f0",
                    verticalAlign: "middle",
                  }}
                >
                  <TableCell
                    sx={{ ...tdSx, color: "#94a3b8", fontSize: "13px", fontWeight: 500 }}
                  >
                    {idx + 1}
                  </TableCell>

                  <TableCell sx={tdSx}>
                    <Typography sx={{ fontWeight: 700, fontSize: "13px", color: "#04343a" }}>
                      {basket.name}
                      {basket.is_locked && (
                        <span style={{ marginLeft: 6, fontSize: 11, color: "#94a3b8" }}>
                          🔒
                        </span>
                      )}
                    </Typography>
                    {basket.description && (
                      <Typography sx={{ fontSize: "11px", color: "#94a3b8", mt: 0.3 }}>
                        {basket.description}
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell sx={tdSx}>
                    <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "#04343a" }}>
                      {basket.strategy || "—"}
                    </Typography>
                  </TableCell>

                  <TableCell sx={tdSx}>
                    <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "#04343a" }}>
                      {basket.risk_level || "—"}
                    </Typography>
                  </TableCell>

                  <TableCell sx={{ ...tdSx, textAlign: "center" }}>
                    <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "#04343a" }}>
                      {basket.total_stocks ?? "—"}
                    </Typography>
                  </TableCell>

                  <TableCell sx={tdSx}>
                    <Typography sx={{ fontSize: "12px", color: "#1e293b" }}>
                      {formatCurrency(basket.minimum_investment)}
                    </Typography>
                  </TableCell>

                  <TableCell sx={tdSx}>
                    <Typography
                      sx={{
                        fontSize: "12px",
                        fontWeight: 700,
                        color:
                          basket.potential_return != null && Number(basket.potential_return) >= 0
                            ? "#1a7f3c"
                            : "#c0392b",
                      }}
                    >
                      {formatPercent(basket.potential_return)}
                    </Typography>
                  </TableCell>

                  <TableCell sx={tdSx}>
                    <StatusBadge
                      status={basket.status}
                      hasActions={(STATUS_ACTIONS[basket.status] || []).length > 0}
                      onClick={(e) =>
                        setStatusMenu({ anchorEl: e.currentTarget, basket })
                      }
                    />
                  </TableCell>

                  <TableCell sx={tdSx}>
                    <Typography
                      sx={{
                        fontSize: "11px",
                        fontWeight: 700,
                        color: basket.is_free ? "#016551" : "#94a3b8",
                      }}
                    >
                      {basket.is_free ? "FREE" : "PAID"}
                    </Typography>
                  </TableCell>

                  <TableCell sx={{ ...tdSx, textAlign: "center" }}>
                    <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                      <Tooltip title="Edit">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => onEdit(basket)}
                            disabled={loadingDetailId === basket.id}
                            sx={{
                              color: "#016551",
                              background: "#f0faf7",
                              "&:hover": { background: "#016551", color: "#fff" },
                              transition: "all 0.2s",
                            }}
                          >
                            <EditIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </span>
                      </Tooltip>

                      {(STATUS_ACTIONS[basket.status] || []).some(
                        (a) => a.action === "CLOSE",
                      ) ? (
                        <Tooltip title="Close Basket">
                          <IconButton
                            size="small"
                            onClick={() =>
                              setConfirmAction({ basket, action: "CLOSE" })
                            }
                            sx={{
                              color: "#c0392b",
                              background: "#fdecea",
                              "&:hover": { background: "#c0392b", color: "#fff" },
                              transition: "all 0.2s",
                            }}
                          >
                            <CancelIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Box sx={{ width: 28, height: 28 }} />
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={!!confirmAction}
        onClose={closeConfirmDialog}
        maxWidth={closeMethod === "MANUAL" ? "sm" : "xs"}
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: "16px" }}>
          {confirmAction ? ACTION_LABELS[confirmAction.action] : ""} Basket
        </DialogTitle>
        <DialogContent>
          {confirmAction?.action === "CLOSE" && !closeMethod ? (
            <Box sx={{ display: "flex", gap: 1.5, mt: 1 }}>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => setCloseMethod("CMP")}
                sx={{ color: "#016551", borderColor: "#016551" }}
              >
                CMP
              </Button>
              <Button
                variant="outlined"
                fullWidth
                onClick={selectManualMethod}
                sx={{ color: "#016551", borderColor: "#016551" }}
              >
                Manual
              </Button>
            </Box>
          ) : confirmAction?.action === "CLOSE" && closeMethod === "MANUAL" ? (
            <>
              {manualLoading ? (
                <Typography sx={{ fontSize: "13px", color: "#94a3b8", mt: 1 }}>
                  Loading holdings...
                </Typography>
              ) : (
                <Box sx={{ mt: 1 }}>
                  {manualHoldings.map((h) => (
                    <Box
                      key={h.symbol}
                      sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontSize: "13px", fontWeight: 700 }}>
                          {h.symbol}
                        </Typography>
                        <Typography sx={{ fontSize: "11px", color: "#94a3b8" }}>
                          Entry: {h.entry_price != null ? `₹${h.entry_price}` : "—"}
                        </Typography>
                      </Box>
                      <TextField
                        size="small"
                        label="Exit Price"
                        value={manualPrices[h.symbol] ?? ""}
                        onChange={(e) =>
                          setManualPrices((prev) => ({
                            ...prev,
                            [h.symbol]: e.target.value.replace(/[^0-9.]/g, ""),
                          }))
                        }
                        sx={{ width: 140 }}
                      />
                    </Box>
                  ))}
                </Box>
              )}
              <TextField
                fullWidth
                multiline
                minRows={2}
                size="small"
                label={manualExitingAll ? "Reason for closing" : "Reason for closing (optional)"}
                value={closeReason}
                onChange={(e) => setCloseReason(e.target.value)}
                placeholder="e.g. Investment thesis achieved, booking profit."
                sx={{ mt: 1.5 }}
              />
            </>
          ) : confirmAction?.action === "CLOSE" ? (
            <TextField
              autoFocus
              fullWidth
              multiline
              minRows={2}
              size="small"
              label="Reason for closing"
              value={closeReason}
              onChange={(e) => setCloseReason(e.target.value)}
              placeholder="e.g. Investment thesis achieved, booking profit."
              sx={{ mt: 1 }}
            />
          ) : (
            <Typography sx={{ fontSize: "13px", color: "#475569" }}>
              Are you sure you want to {ACTION_LABELS[confirmAction?.action]?.toLowerCase()} "
              {confirmAction?.basket?.name}"?
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, display: confirmAction?.action === "CLOSE" && !closeMethod ? "none" : "flex" }}>
          <Button onClick={closeConfirmDialog} sx={{ color: "#64748b" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={
              confirmAction?.action === "CLOSE" &&
              (closeMethod === "MANUAL"
                ? manualLoading ||
                  filledManualHoldings.length === 0 ||
                  (manualExitingAll && !closeReason.trim())
                : !closeReason.trim())
            }
            sx={{
              background: "#016551",
              "&:hover": { background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)" },
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Menu
        anchorEl={statusMenu?.anchorEl}
        open={!!statusMenu}
        onClose={closeStatusMenu}
      >
        {(STATUS_ACTIONS[statusMenu?.basket?.status] || []).map(({ action, status }) => {
          const { color } = STATUS_COLORS[status] || STATUS_COLORS.DRAFT;
          return (
            <MenuItem
              key={action}
              onClick={() => {
                setConfirmAction({ basket: statusMenu.basket, action });
                closeStatusMenu();
              }}
              sx={{ fontSize: "13px", fontWeight: 600, color }}
            >
              {status}
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
};

export default BasketTable;
